import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faGripVertical } from "@fortawesome/free-solid-svg-icons";

import useFetch from "Hooks/useFetch";
import useUserContext from "Hooks/useUserContext";
import useAlertContext from "Hooks/useAlertContext";
import usePageTitle from "Hooks/usePageTitle";
import DimensionSpinner from "components/DimensionSpinner/DimensionSpinner";

import {
    IProviderData,
    ISearchMethod,
} from "Pages/Integration/EditIntegrations2/types";
import InlineEditableCell from "Pages/Integration/EditIntegrations2/Components/InlineEditableCell";
import EditIntegrationModal2 from "Pages/Integration/EditIntegrations2/Components/EditIntegrationModal2";
import AddIntegrationModal2, {
    IAddPayload,
} from "Pages/Integration/EditIntegrations2/Components/AddIntegrationModal2";
import {
    applyIntegrationSystemOverrides,
    saveIntegrationSystemOverride,
} from "Pages/Integration/systemDirectionOverrides";
import {
    normalizeSystemKey,
    parseCompanySystems,
} from "Pages/Integration/systemIcons";

import styles from "Pages/Integration/EditIntegrations2/EditIntegrations2.module.css";

const DIRECTION_OPTIONS = [
    "get",
    "send",
    "link",
    "unlink",
    "compare",
    "report",
    "within",
    "to_both",
    "to_neither",
    "",
];

/** Strip the leaked "Direction." prefix some metadata writes leave behind. */
function normalizeDirection(raw: string | null | undefined): string {
    if (!raw) return "";
    const t = raw.trim();
    return t.startsWith("Direction.") ? t.slice("Direction.".length) : t;
}

export default function EditIntegrations2() {
    usePageTitle("Edit Integrations");
    const [userContext] = useUserContext();
    const { newAlert } = useAlertContext();
    const { get, put, post, deleteRequest } = useFetch();

    const [providerData, setProviderData] = useState<IProviderData[]>();
    const [activeIntegrations, setActiveIntegrations] = useState<IIntegration[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    const [filterProcess, setFilterProcess] = useState("");
    const [filterName, setFilterName] = useState("");

    const [editingId, setEditingId] = useState<number | null>(null);
    const [adding, setAdding] = useState(false);

    // Drag source — ref (not state) so the value is readable synchronously
    // inside dragover/drop handlers that fire before React flushes state.
    const dragSrcIdRef = useRef<number | null>(null);
    // Visual-only state — these can lag a frame, it's fine.
    const [dragSrcId, setDragSrcId] = useState<number | null>(null);
    const [dragOverRowId, setDragOverRowId] = useState<number | null>(null);
    const [dragOverBelow, setDragOverBelow] = useState(false);

    const ignoreFetches = useRef(false);

    const fetchProviderData = useCallback(async () => {
        setIsLoading(true);
        const response = await get(`/api/integrations/edit`);
        const data = await response.json();
        if (response.ok && !ignoreFetches.current) setProviderData(data);
        else if (!response.ok) newAlert({ message: data.detail ?? "Failed to load provider metadata." });
        setIsLoading(false);
    }, [get, newAlert]);

    const fetchActiveIntegrations = useCallback(async () => {
        const response = await get(`/api/integrations`);
        const data = await response.json();
        if (response.ok && !ignoreFetches.current)
            setActiveIntegrations(
                applyIntegrationSystemOverrides(data, userContext.companyId)
            );
        else if (!response.ok) newAlert({ message: data.detail ?? "Failed to load active integrations." });
    }, [get, newAlert, userContext.companyId]);

    useEffect(() => {
        ignoreFetches.current = false;
        fetchProviderData();
        fetchActiveIntegrations();
        return () => {
            ignoreFetches.current = true;
        };
    }, [fetchProviderData, fetchActiveIntegrations]);

    /**
     * Map process name → set of arg counts the provider exposes for that process.
     * A given process may be published with multiple arg-count overloads.
     */
    const providerProcessArgCounts = useMemo(() => {
        const map = new Map<string, Set<number>>();
        if (!providerData) return map;
        for (const p of providerData) {
            for (const integ of p.integrations ?? []) {
                if (!integ.process) continue;
                if (!map.has(integ.process)) map.set(integ.process, new Set());
                map.get(integ.process)!.add(integ.args?.length ?? 0);
            }
        }
        return map;
    }, [providerData]);

    /** Validate one active integration against the provider's published list. */
    function validateRow(row: IIntegration): "ok" | "missing" | "wrong-args" {
        // While provider data is still loading, don't flag anything.
        if (!providerData || providerProcessArgCounts.size === 0) return "ok";
        const allowed = providerProcessArgCounts.get(row.process);
        if (!allowed) return "missing";
        if (!allowed.has(row.num_args ?? 0)) return "wrong-args";
        return "ok";
    }

    /** Group active integrations by integration_group, sorted by list_order. */
    const grouped = useMemo(() => {
        const map = new Map<string, IIntegration[]>();
        for (const i of activeIntegrations) {
            const key = i.integration_group?.trim() || "(uncategorized)";
            if (!map.has(key)) map.set(key, []);
            map.get(key)!.push(i);
        }
        for (const arr of map.values()) {
            arr.sort((a, b) => (a.list_order ?? 0) - (b.list_order ?? 0));
        }
        return Array.from(map.entries()).sort(([a], [b]) =>
            a.localeCompare(b, undefined, { sensitivity: "base" }),
        );
    }, [activeIntegrations]);

    /** Save a partial change to a single integration via PUT (single-element list). */
    const saveRow = useCallback(
        async (row: IIntegration, patch: Partial<IIntegration>) => {
            const next = { ...row, ...patch };
            // Always normalize direction so any drift gets cleaned on save.
            next.direction = normalizeDirection(next.direction);
            const response = await put(`/api/integrations/edit`, [next]);
            if (!response.ok) {
                const data = await response.json().catch(() => ({}));
                newAlert({ message: data.detail ?? "Save failed." });
                return;
            }
            setActiveIntegrations((rows) =>
                rows.map((r) => (r.id === row.id ? next : r)),
            );
        },
        [put, newAlert],
    );

    /** Provider-chain search methods deduped by process — drives the
     * loader dropdown for each argument row in the Edit modal. */
    const searchMethods: ISearchMethod[] = useMemo(() => {
        if (!providerData) return [];
        const seen = new Set<string>();
        return providerData
            .flatMap((p) => p.searches ?? [])
            .filter((s) => s.process && !seen.has(s.process) && seen.add(s.process));
    }, [providerData]);

    /** Existing groups (deduped + sorted) for the Add/Edit combo. */
    const existingGroups = useMemo(() => {
        const seen = new Set<string>();
        for (const r of activeIntegrations) {
            const g = (r.integration_group ?? "").trim();
            if (g) seen.add(g);
        }
        return Array.from(seen).sort((a, b) => a.localeCompare(b));
    }, [activeIntegrations]);

    const systemOptions = useMemo(
        () => [
            { value: "", label: "(auto from direction)" },
            ...parseCompanySystems(userContext.companySystems).map((s) => ({
                value: normalizeSystemKey(s),
                label: s,
            })),
        ],
        [userContext.companySystems]
    );

    /** Save the full Edit modal form. */
    const saveEdit = useCallback(
        async (next: IIntegration) => {
            const response = await put(`/api/integrations/edit`, [next]);
            if (!response.ok) {
                const data = await response.json().catch(() => ({}));
                newAlert({ message: data.detail ?? "Save failed." });
                return;
            }
            await fetchActiveIntegrations();
            setEditingId(null);
        },
        [put, newAlert, fetchActiveIntegrations],
    );

    /** Bulk add. Two-step:
     * 1. POST the simple ISimpleIntegration-shape payload. Server creates rows
     *    with integration_group="New Group" (hardcoded in the controller).
     * 2. After server confirms, refetch and PUT the freshly-created rows with
     *    the user's chosen target group. */
    const submitAdd = useCallback(
        async (payload: IAddPayload[], targetGroup: string) => {
            if (payload.length === 0) {
                setAdding(false);
                return;
            }
            const postResp = await post(`/api/integrations/edit`, payload);
            if (!postResp.ok) {
                const data = await postResp.json().catch(() => ({}));
                newAlert({ message: data.detail ?? "Add failed." });
                return;
            }

            // Fetch fresh state so we know each new row's autogenerated id.
            const listResp = await get(`/api/integrations`);
            const allRows: IIntegration[] = listResp.ok ? await listResp.json() : [];

            const wantedProcesses = new Set(payload.map((p) => p.process.toLowerCase()));
            const newlyCreated = allRows.filter(
                (r) =>
                    wantedProcesses.has((r.process ?? "").toLowerCase()) &&
                    (r.integration_group?.trim() === "New Group" ||
                        (r.integration_group ?? "").trim() === ""),
            );

            if (newlyCreated.length > 0 && targetGroup) {
                const rowsForPut = newlyCreated.map((r) => ({
                    ...r,
                    integration_group: targetGroup,
                }));
                const putResp = await put(`/api/integrations/edit`, rowsForPut);
                if (!putResp.ok) {
                    const data = await putResp.json().catch(() => ({}));
                    newAlert({ message: data.detail ?? "Group assignment failed." });
                }
            }

            await fetchActiveIntegrations();
            setAdding(false);
        },
        [post, get, put, newAlert, fetchActiveIntegrations],
    );

    /** Delete a row after confirm. */
    const deleteRow = useCallback(
        async (row: IIntegration) => {
            // eslint-disable-next-line no-alert
            if (!window.confirm(`Delete '${row.name}' (${row.process})?`)) return;
            const response = await deleteRequest(`/api/integrations/edit/${row.id}`);
            if (!response.ok) {
                const data = await response.json().catch(() => ({}));
                newAlert({ message: data.detail ?? "Delete failed." });
                return;
            }
            setActiveIntegrations((rows) => rows.filter((r) => r.id !== row.id));
        },
        [deleteRequest, newAlert],
    );

    const saveSystemOverride = useCallback(
        async (
            row: IIntegration,
            field: "from_system" | "to_system",
            value: string
        ) => {
            const normalized = normalizeSystemKey(value);

            saveIntegrationSystemOverride(userContext.companyId, row.id, {
                [field]: normalized,
            });

            setActiveIntegrations((rows) =>
                rows.map((r) =>
                    r.id === row.id
                        ? {
                              ...r,
                              [field]: normalized,
                          }
                        : r
                )
            );
        },
        [userContext.companyId]
    );

    /** Persist a single group's order. Rewrites list_order on each row AND
     * forces integration_group to match `groupName` (handles cross-group). */
    const persistGroup = useCallback(
        async (groupName: string, ids: number[], rowsForLookup: IIntegration[]) => {
            if (ids.length === 0) return;
            const rows: IIntegration[] = ids
                .map((id, idx) => {
                    const r = rowsForLookup.find((x) => x.id === id);
                    if (!r) return null;
                    return {
                        ...r,
                        integration_group: groupName === "(uncategorized)" ? "" : groupName,
                        list_order: idx,
                    };
                })
                .filter((r): r is IIntegration => r !== null);
            if (rows.length === 0) return;
            const response = await put(`/api/integrations/edit`, rows);
            if (!response.ok) {
                const data = await response.json().catch(() => ({}));
                newAlert({ message: data.detail ?? "Reorder save failed." });
            }
        },
        [put, newAlert],
    );

    /** Local-first drag handler. Mutates activeIntegrations optimistically,
     * then persists. Supports within-group reorder + cross-group move. */
    const handleDrop = useCallback(
        (srcId: number, destGroupName: string, destRowId: number | null, insertBelow: boolean) => {
            setActiveIntegrations((rows) => {
                const next = [...rows];
                const srcIdx = next.findIndex((r) => r.id === srcId);
                if (srcIdx < 0) return rows;
                const src = next[srcIdx];
                const srcGroupName = (src.integration_group?.trim() || "(uncategorized)");

                // Build the destination group's ordered ids INCLUDING the dragged row at the right position.
                const destExisting = next
                    .filter((r) => (r.integration_group?.trim() || "(uncategorized)") === destGroupName && r.id !== srcId)
                    .sort((a, b) => (a.list_order ?? 0) - (b.list_order ?? 0));

                let insertAt = destExisting.length;
                if (destRowId !== null) {
                    const target = destExisting.findIndex((r) => r.id === destRowId);
                    if (target >= 0) insertAt = insertBelow ? target + 1 : target;
                }
                const destIds = [...destExisting.map((r) => r.id)];
                destIds.splice(insertAt, 0, srcId);

                // Mutate dragged row's group + list_order in `next`
                next[srcIdx] = {
                    ...src,
                    integration_group: destGroupName === "(uncategorized)" ? "" : destGroupName,
                    list_order: insertAt,
                };
                // Renumber destination group
                destIds.forEach((id, i) => {
                    const idx = next.findIndex((r) => r.id === id);
                    if (idx >= 0)
                        next[idx] = {
                            ...next[idx],
                            integration_group: destGroupName === "(uncategorized)" ? "" : destGroupName,
                            list_order: i,
                        };
                });
                // Renumber source group if cross-group
                if (srcGroupName !== destGroupName) {
                    const srcRemaining = next
                        .filter((r) => (r.integration_group?.trim() || "(uncategorized)") === srcGroupName)
                        .sort((a, b) => (a.list_order ?? 0) - (b.list_order ?? 0));
                    srcRemaining.forEach((r, i) => {
                        const idx = next.findIndex((x) => x.id === r.id);
                        if (idx >= 0) next[idx] = { ...next[idx], list_order: i };
                    });
                    // Persist source group too
                    persistGroup(
                        srcGroupName,
                        srcRemaining.map((r) => r.id),
                        next,
                    );
                }
                // Persist destination group
                persistGroup(destGroupName, destIds, next);

                return next;
            });
        },
        [persistGroup],
    );

    /** Rename a whole integration_group — bulk PUT every row in the group. */
    const renameGroup = useCallback(
        async (oldName: string) => {
            // eslint-disable-next-line no-alert
            const proposed = window.prompt(`Rename group "${oldName}" to:`, oldName);
            if (proposed === null) return;
            const newName = proposed.trim();
            if (!newName || newName === oldName) return;
            const rows = activeIntegrations
                .filter((r) => (r.integration_group?.trim() || "(uncategorized)") === oldName)
                .map((r) => ({ ...r, integration_group: newName }));
            if (rows.length === 0) return;
            const response = await put(`/api/integrations/edit`, rows);
            if (!response.ok) {
                const data = await response.json().catch(() => ({}));
                newAlert({ message: data.detail ?? "Rename failed." });
                return;
            }
            setActiveIntegrations((all) =>
                all.map((r) => {
                    const match = rows.find((nr) => nr.id === r.id);
                    return match ?? r;
                }),
            );
        },
        [activeIntegrations, put, newAlert],
    );

    if (isLoading && !providerData) return <DimensionSpinner />;

    const fp = filterProcess.toLowerCase();
    const fn = filterName.toLowerCase();

    const directionOptions = DIRECTION_OPTIONS.map((d) => ({
        value: d,
        label: d === "" ? "(none)" : d,
    }));

    const companyDisplay = userContext.companyName || userContext.companyId;

    // The provider chain always lists this company's own provider class first
    // (Controllers/Admin/EditIntegrations.cs GetRelevantClientProviders seeds the
    // list with the matched clientProvider before appending its base classes), so
    // element 0's dimension_version is the build this screen is editing against.
    // Falls back to an explicit "unknown" rather than a blank when the Provider
    // Service was unreachable and providerData never populated.
    const providerVersion = providerData?.[0]?.dimension_version;

    return (
        <div className={styles.page}>
            <div className={styles.header}>
                <div className={styles.titleGroup}>
                    <h1 className={styles.title}>
                        Edit Integrations for <code>{companyDisplay}</code>
                    </h1>
                    <span
                        className={styles.providerVersion}
                        title="Version of the provider DLL build this screen's metadata was loaded from"
                    >
                        Provider data version:{" "}
                        <code>{providerVersion || "unknown"}</code>
                    </span>
                </div>
                <div className={styles.actions}>
                    <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => {
                            fetchProviderData();
                            fetchActiveIntegrations();
                        }}
                        title="Refetch provider metadata + active integrations"
                        disabled={isLoading}
                    >
                        {isLoading ? "Refreshing…" : "↻ Refresh"}
                    </button>
                    <button
                        type="button"
                        className="btn btn-primary"
                        onClick={() => setAdding(true)}
                        disabled={!providerData}
                        title={
                            providerData
                                ? "Add a new integration to this company"
                                : "Loading provider metadata…"
                        }
                    >
                        + Add Integration
                    </button>
                </div>
            </div>
            <p className={styles.summary}>
                {activeIntegrations.length} integrations in {grouped.length} groups
                {(() => {
                    const flagged = activeIntegrations.filter((r) => validateRow(r) !== "ok").length;
                    if (flagged === 0) return null;
                    return (
                        <span style={{ marginLeft: "0.5em", color: "#dc3545", fontWeight: 500 }}>
                            — {flagged} flagged ⚠
                        </span>
                    );
                })()}
                <span className={styles.summaryHint}>
                    {" "}
                    — drag rows to reorder or move between groups
                </span>
            </p>

            <table className={styles.table}>
                <thead>
                    <tr>
                        <th className={styles.dragTh}></th>
                        <th>Process</th>
                        <th>Name</th>
                        <th>Direction</th>
                        <th>From</th>
                        <th>To</th>
                        <th>Disabled</th>
                        <th>Hidden</th>
                        <th className={styles.argsTh}>Args</th>
                        <th className={styles.actionsTh}></th>
                    </tr>
                    <tr className={styles.filterRow}>
                        <th></th>
                        <th>
                            <input
                                type="search"
                                placeholder="Filter process…"
                                value={filterProcess}
                                onChange={(e) => setFilterProcess(e.currentTarget.value)}
                                autoFocus
                            />
                        </th>
                        <th>
                            <input
                                type="search"
                                placeholder="Filter name…"
                                value={filterName}
                                onChange={(e) => setFilterName(e.currentTarget.value)}
                            />
                        </th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                    </tr>
                </thead>
                {grouped.map(([groupName, rows]) => {
                    const visibleRows = rows.filter((r) => {
                        if (fp) {
                            const processMatch = (r.process ?? "").toLowerCase().includes(fp);
                            const argMatch = (r.arguments ?? []).some((a) =>
                                (a.name ?? "").toLowerCase().includes(fp),
                            );
                            if (!processMatch && !argMatch) return false;
                        }
                        if (fn && !(r.name ?? "").toLowerCase().includes(fn)) return false;
                        return true;
                    });
                    if ((fp || fn) && visibleRows.length === 0) return null;
                    return (
                        <tbody
                            key={groupName}
                            className={styles.group}
                            data-group-name={groupName}
                            onDragOver={(e) => {
                                if (dragSrcIdRef.current === null) return;
                                const onRow = (e.target as HTMLElement).closest(
                                    "tr[data-row-id]",
                                );
                                if (onRow) return;
                                e.preventDefault();
                            }}
                            onDrop={(e) => {
                                const src = dragSrcIdRef.current;
                                if (src === null) return;
                                const onRow = (e.target as HTMLElement).closest(
                                    "tr[data-row-id]",
                                );
                                if (onRow) return;
                                e.preventDefault();
                                handleDrop(src, groupName, null, true);
                                dragSrcIdRef.current = null;
                                setDragSrcId(null);
                                setDragOverRowId(null);
                            }}
                        >
                            <tr className={styles.groupHeader}>
                                <th colSpan={9}>
                                    <span className={styles.groupName}>{groupName}</span>{" "}
                                    <span className={styles.groupCount}>({rows.length})</span>
                                </th>
                                <th className={styles.actionsTh}>
                                    <button
                                        type="button"
                                        className={styles.linkBtn}
                                        onClick={() => renameGroup(groupName)}
                                        title="Rename this group across all rows"
                                    >
                                        Rename
                                    </button>
                                </th>
                            </tr>
                            {visibleRows.map((row) => (
                                <tr
                                    key={row.id}
                                    data-row-id={row.id}
                                    className={`${dragSrcId === row.id ? styles.dragging : ""} ${
                                        dragOverRowId === row.id
                                            ? dragOverBelow
                                                ? styles.dragOverBottom
                                                : styles.dragOverTop
                                            : ""
                                    }`}
                                    onDragOver={(e) => {
                                        const src = dragSrcIdRef.current;
                                        if (src === null || src === row.id) return;
                                        e.preventDefault();
                                        const rect = e.currentTarget.getBoundingClientRect();
                                        const below = e.clientY - rect.top > rect.height / 2;
                                        if (dragOverRowId !== row.id) setDragOverRowId(row.id);
                                        if (dragOverBelow !== below) setDragOverBelow(below);
                                    }}
                                    onDragLeave={() => {
                                        if (dragOverRowId === row.id) {
                                            setDragOverRowId(null);
                                        }
                                    }}
                                    onDrop={(e) => {
                                        const src = dragSrcIdRef.current;
                                        if (src === null || src === row.id) return;
                                        e.preventDefault();
                                        const rect = e.currentTarget.getBoundingClientRect();
                                        const below = e.clientY - rect.top > rect.height / 2;
                                        handleDrop(src, groupName, row.id, below);
                                        dragSrcIdRef.current = null;
                                        setDragSrcId(null);
                                        setDragOverRowId(null);
                                    }}
                                >
                                    <td
                                        className={styles.dragHandle}
                                        draggable
                                        title="Drag to reorder or move between groups"
                                        onDragStart={(e) => {
                                            dragSrcIdRef.current = row.id;
                                            setDragSrcId(row.id);
                                            e.dataTransfer.effectAllowed = "move";
                                            // Firefox + some Chromium builds need setData to actually initiate the drag.
                                            e.dataTransfer.setData("text/plain", String(row.id));
                                            // Drag image is the entire row, not just the cell
                                            const tr = e.currentTarget.parentElement;
                                            if (tr) e.dataTransfer.setDragImage(tr, 10, 10);
                                        }}
                                        onDragEnd={() => {
                                            dragSrcIdRef.current = null;
                                            setDragSrcId(null);
                                            setDragOverRowId(null);
                                        }}
                                    >
                                        <FontAwesomeIcon icon={faGripVertical} />
                                    </td>
                                    <td>
                                        <code>{row.process}</code>
                                        {row.arguments && row.arguments.length > 0 && (
                                            <div className={styles.addArgs}>
                                                {row.arguments.map((a) => a.name).join(", ")}
                                            </div>
                                        )}
                                        {(() => {
                                            const status = validateRow(row);
                                            if (status === "ok") return null;
                                            const allowed = providerProcessArgCounts.get(row.process);
                                            const tip =
                                                status === "missing"
                                                    ? `Process "${row.process}" is not in the provider's published integration list.`
                                                    : `Process expects ${
                                                          allowed ? Array.from(allowed).sort().join(" or ") : "?"
                                                      } args, but this integration has ${row.num_args ?? 0}.`;
                                            return (
                                                <span
                                                    title={tip}
                                                    style={{
                                                        marginLeft: "0.4em",
                                                        color: status === "missing" ? "#dc3545" : "#e6a817",
                                                        cursor: "help",
                                                    }}
                                                    aria-label={tip}
                                                >
                                                    ⚠
                                                </span>
                                            );
                                        })()}
                                    </td>
                                    <td>
                                        <InlineEditableCell
                                            kind="text"
                                            field="name"
                                            rowId={row.id}
                                            value={row.name ?? ""}
                                            onSave={(v) => saveRow(row, { name: v })}
                                        />
                                    </td>
                                    <td>
                                        <InlineEditableCell
                                            kind="select"
                                            field="direction"
                                            rowId={row.id}
                                            value={normalizeDirection(row.direction)}
                                            options={directionOptions}
                                            onSave={(v) => saveRow(row, { direction: v })}
                                        />
                                    </td>
                                    <td>
                                        <InlineEditableCell
                                            kind="select"
                                            field="from_system"
                                            rowId={row.id}
                                            value={normalizeSystemKey(row.from_system)}
                                            options={systemOptions}
                                            onSave={(v) =>
                                                saveSystemOverride(row, "from_system", v)
                                            }
                                        />
                                    </td>
                                    <td>
                                        <InlineEditableCell
                                            kind="select"
                                            field="to_system"
                                            rowId={row.id}
                                            value={normalizeSystemKey(row.to_system)}
                                            options={systemOptions}
                                            onSave={(v) =>
                                                saveSystemOverride(row, "to_system", v)
                                            }
                                        />
                                    </td>
                                    <td>
                                        <InlineEditableCell
                                            kind="select"
                                            field="disabled"
                                            rowId={row.id}
                                            value={row.disabled ? "true" : "false"}
                                            displayLabel={row.disabled ? "Disabled" : "Enabled"}
                                            options={[
                                                { value: "false", label: "Enabled" },
                                                { value: "true", label: "Disabled" },
                                            ]}
                                            onSave={(v) => saveRow(row, { disabled: v === "true" })}
                                        />
                                    </td>
                                    <td>
                                        <InlineEditableCell
                                            kind="select"
                                            field="hidden"
                                            rowId={row.id}
                                            value={row.hidden ? "true" : "false"}
                                            displayLabel={row.hidden ? "Hidden" : "Visible"}
                                            options={[
                                                { value: "false", label: "Visible" },
                                                { value: "true", label: "Hidden" },
                                            ]}
                                            onSave={(v) => saveRow(row, { hidden: v === "true" })}
                                        />
                                    </td>
                                    <td className={styles.argsTd}>{row.num_args}</td>
                                    <td className={styles.actionsTd}>
                                        <button
                                            type="button"
                                            className={styles.linkBtn}
                                            onClick={() => setEditingId(row.id)}
                                            title="Edit integration"
                                        >
                                            Edit
                                        </button>
                                        <button
                                            type="button"
                                            className={`${styles.linkBtn} ${styles.linkBtnDanger}`}
                                            onClick={() => deleteRow(row)}
                                            title="Delete this integration"
                                        >
                                            Delete
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    );
                })}
            </table>

            {editingId !== null && (
                <EditIntegrationModal2
                    integration={activeIntegrations.find((r) => r.id === editingId) ?? null}
                    existingGroups={existingGroups}
                    searchMethods={searchMethods}
                    onClose={() => setEditingId(null)}
                    onSave={saveEdit}
                />
            )}

            {adding && (
                <AddIntegrationModal2
                    providerData={providerData ?? []}
                    activeIntegrations={activeIntegrations}
                    existingGroups={existingGroups}
                    companyId={userContext.companyId}
                    onClose={() => setAdding(false)}
                    onSubmit={submitAdd}
                />
            )}
        </div>
    );
}
